import { Resources, useResource } from "Infrastructure/Translation/Resources";
import { useHandleServerError } from "Infrastructure/Utilities/Forms/Hooks/useHandleServerError";
import { useCreateRegionMutation } from "Market/Api/Mutation/createRegionMutation";
import {
	getRegionsFormSchema,
	type RegionsFormSchema,
} from "Market/Components/Form/Schema/createRegionsFormSchema";
import Input from "Shared/Components/Form/CustomFormComponent/Input";
import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Button, Stack, Typography } from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import { isEmpty } from "lodash";
import { useFieldArray, useForm } from "react-hook-form";

type RegionsFormProps = {
	marketId: number;
	onSuccess: () => void;
	isSkippble?: boolean;
};

const marketFormResource = Resources.base.market.forms;

const defaultFormValues: RegionsFormSchema = {
	regions: [{ name: "", regionCode: "" }],
};

export function RegionsBaseForm({
	marketId,
	onSuccess,
	isSkippble,
}: RegionsFormProps) {
	const { t } = useResource();

	const form = useForm<RegionsFormSchema>({
		resolver: zodResolver(getRegionsFormSchema(t)),
		defaultValues: defaultFormValues,
	});

	const {
		control,
		formState: { errors },
	} = form;
	const { mutate: createRegion, isPending } = useCreateRegionMutation(marketId);
	const { handleServerError } = useHandleServerError(form);

	const { regions } = form.watch();

	const disableSaveButton =
		!isEmpty(errors) || !regions[0]?.name || !regions[0]?.regionCode;

	const handleSubmitCreate = () => {
		handleServerError(({ regions: [region] }) => createRegion(region), {
			onSuccess: () => {
				onSuccess();
				form.reset(defaultFormValues);
			},
		});
	};
	const { fields } = useFieldArray({
		control,
		name: "regions",
	});

	return (
		<form style={{ marginTop: 0 }}>
			<Typography variant="h5">Add new region</Typography>
			<Grid2 container spacing={1} mb={2} mt={0.5}>
				{fields.map((field, index) => (
					<Grid2 key={field.id} container columnSpacing={2} size={{ xs: 12 }}>
						<Stack flexDirection="row" gap={2} width="100%">
							<Box width="100%">
								<Input
									id={`name-${index}`}
									name={`regions.${index}.name`}
									label={t(marketFormResource.region.name)}
									sxLabel={{ marginTop: 0 }}
									variant="outlined"
									control={control}
									fullWidth
									errors={errors}
								/>
							</Box>
							<Box width="100%">
								<Input
									id={`region-code-${index}`}
									name={`regions.${index}.regionCode`}
									label={t(marketFormResource.labels.regionCode)}
									sxLabel={{ marginTop: 0 }}
									variant="outlined"
									control={control}
									fullWidth
									errors={errors}
									labelTooltip="Region code in format: <country code><united nations - UN/LOCODE>. Example: SKBRA. Follow: https://service.unece.org/trade/locode/gb.htm"
								/>
							</Box>
						</Stack>
					</Grid2>
				))}

				{!isSkippble && (
					<Grid2 size={{ xs: 12 }}>
						<Button
							variant="contained"
							onClick={handleSubmitCreate}
							loading={isPending}
							disabled={disableSaveButton}
						>
							{t(marketFormResource.save)}
						</Button>
					</Grid2>
				)}
			</Grid2>
			{isSkippble && (
				<Stack direction={"row"} justifyContent={"end"} columnGap={2}>
					<Button
						variant="contained"
						onClick={handleSubmitCreate}
						loading={isPending}
						disabled={disableSaveButton}
					>
						{t(marketFormResource.save)}
					</Button>
					<Button variant="contained" color="primary" onClick={onSuccess}>
						{t(marketFormResource.skip)}
					</Button>
				</Stack>
			)}
		</form>
	);
}
